import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  try {
    const res = await fetch(url, {
      method,
      headers: data ? { "Content-Type": "application/json" } : {},
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

    await throwIfResNotOk(res);
    return res;
  } catch (error: any) {
    // More specific error handling to prevent unhandled rejections
    const errorMessage = error?.message || String(error) || 'Unknown error';
    
    // Don't log certain expected errors
    if (!errorMessage.includes('Failed to fetch') && 
        !errorMessage.includes('429') && 
        !errorMessage.includes('Too Many Requests') &&
        !errorMessage.includes('NetworkError')) {
      console.error(`API request failed for ${method} ${url}:`, error);
    }
    
    // Ensure we always throw a proper Error object
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(errorMessage);
    }
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    try {
      const res = await fetch(queryKey[0] as string, {
        credentials: "include",
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);
      return await res.json();
    } catch (error: any) {
      // Only log errors that aren't expected/temporary
      if (!error.message?.includes('429') && 
          !error.message?.includes('Too Many Requests') &&
          !error.message?.includes('Failed to fetch') &&
          !error.message?.includes('NetworkError')) {
        console.error(`Query failed for ${queryKey[0]}:`, error);
      }
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes for properties data
      gcTime: 30 * 60 * 1000, // 30 minutes cache (renamed from cacheTime in v5)
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.message?.includes('4')) return false;
        // Don't retry on rate limiting errors
        if (error?.message?.includes('429') || error?.message?.includes('Too Many Requests')) return false;
        // Don't retry on network errors that are likely temporary
        if (error?.message?.includes('Failed to fetch') && failureCount >= 1) return false;
        // Only retry network errors up to 1 time
        return failureCount < 1;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: (failureCount, error: any) => {
        // Don't retry mutations on most errors to prevent duplicate operations
        if (error?.message?.includes('429') || error?.message?.includes('Too Many Requests')) return false;
        return failureCount < 1;
      },
    },
  },
});

// Specialized query configurations for different data types
export const queryConfigs = {
  properties: {
    staleTime: 10 * 60 * 1000, // 10 minutes for properties

    gcTime: 30 * 60 * 1000, // 30 minutes cache (renamed from cacheTime in v5)


    refetchOnWindowFocus: false,
  },
  pricing: {
    staleTime: 2 * 60 * 1000, // 2 minutes for pricing

    gcTime: 10 * 60 * 1000, // 10 minutes cache (renamed from cacheTime in v5)



    refetchOnWindowFocus: false,
  },
  availability: {
    staleTime: 1 * 60 * 1000, // 1 minute for availability

    gcTime: 5 * 60 * 1000, // 5 minutes cache (renamed from cacheTime in v5)



    refetchOnWindowFocus: false,
  },
};